import React, { ComponentProps, ReactElement, ReactNode } from 'react';

import PropTypes from 'prop-types';

import ActivityCard from '../ActivityCard';
import ListRow, { ListRowProps } from '../layout/ListRow';

type Item = {
  value: ReactNode;
  label: ReactNode;
};

type AggregatedActivityCardProps = ComponentProps<typeof ActivityCard> & {
  items?: Item[];
  renderRow?: (props: ListRowProps) => ReactElement;
};

/**
 * AggregatedActivityCard component. Describes a single activity with multiple changes inside.
 */
const AggregatedActivityCard = React.forwardRef<
  HTMLDivElement,
  AggregatedActivityCardProps
  // WPLT-1319
  // eslint-disable-next-line @adsk/react-render-props/render-props-as-components
>(({ items = [], renderRow: ListRowComponent = ListRow, ...props }, ref) => {
  if (!items.length) {
    return null;
  }
  return (
    <ActivityCard ref={ref} {...props}>
      <div
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 28,
          },
        ]}
      >
        {ListRowComponent &&
          items.map((item, index) => (
            <ListRowComponent key={index} {...item} />
          ))}
      </div>
    </ActivityCard>
  );
});

AggregatedActivityCard.displayName = 'AggregatedActivityCard';

AggregatedActivityCard.propTypes = {
  ...ActivityCard.propTypes,
  /** Render custom row content. Props: ({ items }) */
  renderRow: PropTypes.func,
  /** Array of items to render inside this card list */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** Label for the item */
      label: PropTypes.node.isRequired,
      /** Value for the item */
      value: PropTypes.node.isRequired,
    }).isRequired
  ),
};

export default Object.assign(AggregatedActivityCard, {
  ListRow,
});
